/**
 * Retrieves the value associated with the given key from a Map.
 *
 * @param map - A Map instance to retrieve the value from.
 * @param key - The key whose associated value is to be returned.
 * @returns - The value associated with the specified key.
 * @throws - If the key is not found in the map.
 */
export function safeGet<K, V>(map: Map<K, V>, key: K | undefined): V;

/**
 * Retrieves the value associated with the given key from a WeakMap.
 *
 * @param map - A WeakMap instance to retrieve the value from.
 * @param key - The key whose associated value is to be returned.
 * @returns - The value associated with the specified key.
 * @throws - If the key is not found in the weakmap.
 */
export function safeGet<K extends object, V>(
  map: WeakMap<K, V>,
  key: K | undefined,
): V;
/**
 * Implementation of the safeGet function for both Map and WeakMap.
 *
 * @param map - The Map or WeakMap instance to retrieve the value from.
 * @param key - The key whose associated value is to be returned.
 * @returns - The value associated with the specified key.
 * @throws - If the key is not found in the map.
 */
export function safeGet<K extends object, V>(
  map: Map<K, V> | WeakMap<K, V>,
  key: K | undefined,
): V {
  if (!key && typeof key !== 'string') {
    throw new Error(`Key: ${key} not found for: ${JSON.stringify(map)}`);
  }
  const value = map.get(key);

  if (value === undefined || value === null) {
    throw new Error(`Value not found for key: ${JSON.stringify(key)}`);
  }

  return value;
}
